import ExcelJS from 'exceljs'
import dayjs from 'dayjs'
import { EtcOutboundEditableRow } from '@/services/outbound/outboundEtc/types'
import { EXCEL_COLUMNS, IMPORTABLE_KEYS } from '@/pages/outbound/outboundEtc/components/columns'

const titleToKey = new Map(EXCEL_COLUMNS.map(({ title, key }) => [title, key]))
const importable = new Set(IMPORTABLE_KEYS)

/**
 * 오류 셀 판별. `#REF!`·`#DIV/0!`·`#N/A` 는 셀 자체가 `{ error }` 이거나 수식 결과가 `{ error }` 다
 * — exceljs 4.4.0 실측. `String()` 으로 떨어뜨리면 `"[object Object]"` 가 값이 되고, 공백이 아니라서
 * 필수값 검사도 BE `@NotBlank` 도 통과해 그대로 저장된다. 빈칸으로 바꿔 필수값 검사가 그 행을 잡게 한다.
 */
const isErrorValue = (value: unknown) => typeof value === 'object' && value !== null && 'error' in value

/**
 * 셀 값을 문자열로 읽는다.
 *
 * ExcelJS `cell.value` 는 객체를 네 가지로 돌려준다 — 날짜(`Date`), 하이퍼링크(`{text, hyperlink}`),
 * 서식 있는 텍스트(`{richText: [...]}`), 수식(`{formula, result}`). `text` 만 보고 나머지를
 * `String(raw)` 로 떨어뜨리면 `"[object Object]"` 가 값으로 들어간다. 공백이 아니라서 필수값 검사도
 * BE `@NotBlank` 도 통과해 그대로 저장된다. 담당자가 양식에서 주문번호를 수식으로 만들거나 서식 있는
 * 텍스트를 붙여넣는 건 흔한 일이라 네 가지를 모두 받는다.
 */
const cellText = (raw: ExcelJS.CellValue) => {
  if (raw == null) return ''
  // Date 도 object 라 객체 분기보다 먼저 본다.
  if (raw instanceof Date) return dayjs(raw).format('YYYY-MM-DD')
  if (typeof raw === 'object') {
    if (isErrorValue(raw)) return ''
    if ('richText' in raw) return (raw as ExcelJS.CellRichTextValue).richText.map((run) => run.text).join('')
    // 수식은 두 모양으로 온다. 채워 내린 수식(shared formula)의 두 번째 행부터는 `formula` 없이
    // `{ sharedFormula, result }` 로 온다 — exceljs 4.4.0 실측.
    // `formula` 만 보면 첫 행만 읽히고 나머지가 `[object Object]` 가 된다.
    if ('formula' in raw || 'sharedFormula' in raw) {
      const { result } = raw as ExcelJS.CellFormulaValue
      if (isErrorValue(result)) return ''
      return result instanceof Date ? dayjs(result).format('YYYY-MM-DD') : String(result ?? '')
    }
    // 하이퍼링크 셀은 `{text, hyperlink}` 인데, 그 `text` 가 다시 서식 있는 텍스트일 수 있다 —
    // `{ text: { richText: [...] }, hyperlink }`(exceljs 4.4.0 실측). 최상위에 `richText` 키가 없어
    // 위 분기에 안 걸리므로 여기서 한 번 더 태운다. `String()` 으로 두면 `"[object Object]"` 가 된다.
    if ('text' in raw) return cellText((raw as { text: ExcelJS.CellValue }).text)
  }
  return String(raw)
}

/**
 * 엑셀 업로드 파싱. 1행 한글 헤더를 컬럼 키로 바꾸고 데이터 행을 신규 행으로 만든다.
 *
 * `IMPORTABLE_KEYS`(편집 셀 20개 + 상품정보 8개)만 받는다 — 나머지 표시 전용 컬럼(상품코드·생성일자 등)은 BE 가 읽지 않는다.
 * `option_code` 는 BE 가 `Long` 으로 받아 정수가 아니면 저장 전체가 거부되므로 여기서 행 번호와 함께 막는다.
 * `IF_REQ` 는 값이 없으면 true 로 채운다(1.0 `CommonUtil.js:193` 의 `GI35_HEAD.IF_REQ = true`).
 */
export const parseEtcOutboundExcel = async (file: File): Promise<Partial<EtcOutboundEditableRow>[]> => {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const sheet = workbook.worksheets[0]
  if (!sheet) return []

  const headerKeys: (string | null)[] = []
  sheet.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    const key = titleToKey.get(cellText(cell.value).trim()) ?? null
    headerKeys[col] = key && importable.has(key) ? key : null
  })
  if (!headerKeys.some(Boolean)) {
    throw new Error('헤더를 인식할 수 없습니다. 엑셀 다운로드 양식과 동일한 1행 헤더가 필요합니다.')
  }

  const rows: Partial<EtcOutboundEditableRow>[] = []
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return

    const item: Record<string, unknown> = {}
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const text = cellText(cell.value).trim()
      if (!text) return
      hasValue = true
      if (key === 'ORD_QTY') item[key] = Number(text) || 0
      else if (key === 'option_code') {
        // 2^53 을 넘으면 Number 가 다른 값으로 반올림한다 — 막지 않으면 엉뚱한 옵션코드로 저장된다.
        if (!/^\d+$/.test(text) || !Number.isSafeInteger(Number(text))) throw new Error(`${rowNumber}행 옵션코드는 숫자여야 합니다.`)
        item[key] = Number(text)
      } else if (key === 'IF_REQ') item[key] = text.toUpperCase() === 'N' ? 'N' : 'Y'
      else item[key] = text
    })

    if (!hasValue) return
    // 1.0 업로드 기본값 — 인터페이스 구분이 비면 Y 로 본다.
    if (item.IF_REQ === undefined) item.IF_REQ = 'Y'
    rows.push(item as Partial<EtcOutboundEditableRow>)
  })

  return rows
}
